import {
  type ContractDetail,
  FUNDING_SOURCES,
  type FundingSource,
  PAYMENT_BASES,
  PAYMENT_PRIORITIES,
  PAYMENT_REQUEST_STATUSES,
  type PaymentPriority,
  type PaymentRequestStatus,
  type PaymentRequestView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { FileLink } from '../../components/FileLink';
import { api } from '../../lib/api';
import { date, dateTime, tenge } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';

/** Выбор «вне графика платежей договора» */
const NONE = '__none';

/** Свежая карточка заявки на оплату: после загрузки файла или решения список может отстать */
export const prKey = (id: string) => ['procurement', 'payment-request', id];

export function usePaymentRequest(initial: PaymentRequestView): PaymentRequestView {
  const q = useQuery({
    queryKey: prKey(initial.id),
    queryFn: () => api<PaymentRequestView>(`/procurement/payment-requests/${initial.id}`),
    initialData: initial,
    staleTime: 0,
  });
  return q.data;
}

function refreshPayments(qc: ReturnType<typeof useQueryClient>, v?: PaymentRequestView) {
  if (v) qc.setQueryData(prKey(v.id), v);
  for (const k of ['procurement', 'finance', 'contracts', 'payments', 'finplan', 'project'])
    qc.invalidateQueries({ queryKey: [k] });
}

/** Подпись планового платежа договора для выбора в заявке на оплату */
export function scheduleOption(r: ContractDetail['schedule'][number]): string {
  const parts: string[] = [];
  parts.push(r.description || (r.basis ? PAYMENT_BASES[r.basis] : 'Платёж'));
  if (r.percent != null) parts.push(`${r.percent} %`);
  if (r.amount != null) parts.push(tenge(r.amount));
  if (r.plannedDate) parts.push(`до ${date(r.plannedDate)}`);
  if (r.requested != null && r.requested > 0) parts.push(`в заявках ${tenge(r.requested)}`);
  return parts.join(', ');
}

/** Открытые плановые платежи договора: не отмечены выполненными и не закрыты заявками целиком */
export function openPaymentRows(d: ContractDetail | undefined) {
  return (d?.schedule ?? []).filter(
    (r) => r.kind === 'payment' && !r.done && (r.amount == null || (r.requested ?? 0) + 0.5 < r.amount),
  );
}

/* ───────────────────────── Комплект документов (ФН-19, ДГ-12) ───────────────────────── */

export function PaymentDocsList({ pr, editable }: { pr: PaymentRequestView; editable: boolean }) {
  const qc = useQueryClient();
  const mark = useMutation({
    mutationFn: (v: { key: string; ok: boolean }) =>
      api<PaymentRequestView>(`/procurement/payment-requests/${pr.id}/docs`, { body: v }),
    onSuccess: (v) => refreshPayments(qc, v),
  });
  const upload = useMutation({
    mutationFn: (v: { key: string; file: File }) => {
      const form = new FormData();
      form.append('entityType', 'payment_request');
      form.append('entityId', pr.id);
      form.append('kind', v.key);
      form.append('file', v.file);
      return api('/files', { form, method: 'POST' });
    },
    onSuccess: () => {
      refreshPayments(qc);
      qc.invalidateQueries({ queryKey: prKey(pr.id) });
      toast('Файл приложен к документу');
    },
  });
  const other = pr.otherFiles.length ? (
    <div className="text-[13px]">
      <p className="text-ink-2">Файлы заявки, приложенные до справочника комплектов:</p>
      <ul className="mt-1 flex flex-col gap-0.5">
        {pr.otherFiles.map((f) => (
          <li key={f.id}>
            <FileLink file={f} />
          </li>
        ))}
      </ul>
    </div>
  ) : null;
  if (!pr.docs.length)
    return (
      <section className="flex flex-col gap-2 border-t border-line pt-4 text-[14px] text-ink-2">
        По справочнику для этого договора и типа платежа документы не нужны.
        {other}
      </section>
    );
  const byFile = pr.docsRequireFile && (pr.status === 'draft' || pr.status === 'approval');
  return (
    <section className="flex flex-col gap-3 border-t border-line pt-4">
      <h3 className="text-[14px] font-semibold">
        Комплект документов для оплаты{' '}
        <span className={clsx('font-normal', pr.docsComplete ? 'text-success' : 'text-warning')}>
          {pr.docsComplete ? '— полный' : '— неполный: на оплату и в 1С не уходит'}
        </span>
      </h3>
      {byFile ? (
        <p className="text-[13px] text-ink-2">
          Документ считается полученным, когда к нему приложен файл (так решил финансовый менеджер).
        </p>
      ) : null}
      <ul className="flex flex-col divide-y divide-line rounded-md border border-line">
        {pr.docs.map((d) => {
          const meta = d.auto ? (
            <span className="block text-[12px] text-ink-2">отмечено само: договор подписан</span>
          ) : d.ok && d.byName ? (
            <span className="block text-[12px] text-ink-2">{`${d.byName}, ${dateTime(d.at)}`}</span>
          ) : !d.ok && byFile ? (
            <span className="block text-[12px] text-warning">нужен файл</span>
          ) : null;
          return (
            <li key={d.key} className="flex flex-col gap-1.5 px-3 py-1.5">
              <div className="flex items-start gap-3">
                {byFile || d.auto || !editable ? (
                  <div className="flex min-h-11 min-w-0 flex-1 items-start gap-3 py-1.5 text-[14px] md:min-h-0">
                    <span aria-hidden className={clsx('w-4 shrink-0', d.ok ? 'text-success' : 'text-ink-3')}>
                      {d.ok ? '✓' : '—'}
                    </span>
                    <span className="min-w-0">
                      <span className="text-ink">{d.title}</span>
                      <span className="sr-only">{d.ok ? ': получен' : ': не получен'}</span>
                      {meta}
                    </span>
                  </div>
                ) : (
                  // Нажимается вся строка с названием: на телефоне флажок 16 px не попасть пальцем
                  <label className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-start gap-3 py-1.5 text-[14px] md:min-h-0">
                    <input
                      type="checkbox"
                      className="mt-1 size-4 shrink-0"
                      checked={d.ok}
                      disabled={mark.isPending}
                      onChange={(e) => mark.mutate({ key: d.key, ok: e.target.checked })}
                    />
                    <span className="min-w-0">
                      <span className="text-ink">{d.title}</span>
                      {meta}
                    </span>
                  </label>
                )}
                {editable && !d.auto ? (
                  <label
                    className="inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2 text-[13px] text-ink-2 hover:bg-tile hover:text-ink md:min-h-8"
                    title={`Приложить файл: ${d.title}`}
                  >
                    <input
                      type="file"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) upload.mutate({ key: d.key, file });
                        e.target.value = '';
                      }}
                    />
                    <Paperclip className="size-4" aria-hidden />
                    Файл
                  </label>
                ) : null}
              </div>
              {d.files.length ? (
                <ul className="mb-1 ml-7 flex flex-col gap-0.5 text-[13px]">
                  {d.files.map((f) => (
                    <li key={f.id}>
                      <FileLink file={f} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
      {other}
      <ErrorBox error={mark.error ?? upload.error} />
    </section>
  );
}

/* ───────────────────────── Повторная оплата (ФН-21) ───────────────────────── */

export function DuplicatesPanel({
  pr,
  canDecide,
  canRequest,
}: {
  pr: PaymentRequestView;
  canDecide: boolean;
  canRequest: boolean;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const act = useMutation({
    mutationFn: (body: { action: 'release' | 'cancel'; reason: string }) =>
      api<PaymentRequestView>(`/procurement/payment-requests/${pr.id}/action`, { body }),
    onSuccess: (v) => {
      refreshPayments(qc, v);
      toast(`${v.number}: ${PAYMENT_REQUEST_STATUSES[v.status].toLowerCase()}`);
      setReason('');
    },
  });
  const list = pr.duplicates.length ? (
    <ul className="mt-1 list-disc pl-5">
      {pr.duplicates.map((x) => (
        <li key={x.id}>
          {x.kind === 'fact'
            ? `оплата без заявки${x.number ? ` по документу ${x.number}` : ''}`
            : (x.number ?? 'заявка на оплату')}
          {' · '}
          {x.projectCode ?? 'в другом проекте'}
          {x.kind === 'request'
            ? ` · ${(PAYMENT_REQUEST_STATUSES[x.status as PaymentRequestStatus] ?? x.status).toLowerCase()}`
            : ''}
          {x.date ? ` · ${date(x.date)}` : ''}
        </li>
      ))}
    </ul>
  ) : null;
  const kinds = pr.hold?.kinds ?? [];
  const parts = [
    kinds.includes('match') ? 'счёт не сходится с договором или приходом' : null,
    kinds.includes('duplicate') ? 'возможная повторная оплата' : null,
    kinds.includes('limit') ? 'платёж сверх лимита платежей компании' : null,
  ].filter((x): x is string => x !== null);
  const what = parts.join('; ');
  // Только расхождение со счётом: решения финансового менеджера нет, остановку снимает сверка (ФН-20)
  const matchOnly = kinds.length === 1 && kinds[0] === 'match';
  if (pr.status === 'hold' && matchOnly)
    return (
      <section className="rounded-md bg-danger-soft px-4 py-3 text-[14px] text-ink">
        <b>Остановлена: {what}.</b> {pr.hold?.reason}
        <p className="mt-1 text-[13px] text-ink-2">
          Подробности и кнопка «Сверить заново» — в блоке сверки ниже.
        </p>
      </section>
    );
  if (pr.status === 'hold')
    return (
      <section className="flex flex-col gap-3 rounded-md bg-danger-soft px-4 py-3 text-[14px] text-ink">
        <div>
          <b>Остановлена: {what}.</b> {pr.hold?.reason}
          {list}
        </div>
        {canDecide ? (
          <>
            <Field
              label="Решение финансового менеджера"
              hint={
                kinds.includes('limit')
                  ? 'Если платёж можно сдвинуть, перенесите плановую дату с причиной ниже, затем разрешите оплату'
                  : undefined
              }
            >
              <Textarea
                rows={2}
                value={reason}
                placeholder={
                  kinds.includes('duplicate')
                    ? 'Почему это не повторная оплата, например: второй этап по тому же счёту'
                    : 'Почему платёж проходит сверх лимита, например: скидка поставщика при оплате в этот день'
                }
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                loading={act.isPending}
                disabled={reason.trim().length < 3}
                onClick={() => act.mutate({ action: 'release', reason: reason.trim() })}
              >
                Разрешить оплату
              </Button>
              <Button
                variant="danger"
                loading={act.isPending}
                disabled={reason.trim().length < 3}
                onClick={() => act.mutate({ action: 'cancel', reason: reason.trim() })}
              >
                Отменить заявку
              </Button>
            </div>
          </>
        ) : (
          <p className="text-[13px] text-ink-2">
            Решение — за финансовым менеджером: заявка в «Ждёт меня» у него.
            {canRequest && kinds.includes('duplicate')
              ? ' Если это ошибка в заявке, отмените её и создайте новую.'
              : ''}
          </p>
        )}
        <ErrorBox error={act.error} />
      </section>
    );
  if (pr.status === 'draft' && pr.duplicates.length)
    return (
      <section className="rounded-md bg-warning-soft px-4 py-3 text-[14px] text-ink">
        <b>Похоже на повторную оплату:</b> у контрагента уже есть оплата на ту же сумму — с тем же счётом,
        назначением или по тому же договору. При передаче на оплату заявка остановится на проверку финансового
        менеджера (ФН-21).
        {list}
      </section>
    );
  if (pr.hold?.decision)
    return (
      <p className="text-[13px] text-ink-2">
        Остановка снята финансовым менеджером ({pr.hold.reason.toLowerCase()}): {pr.hold.decision} —{' '}
        {pr.hold.decidedByName ?? '—'}, {dateTime(pr.hold.decidedAt)}
      </p>
    );
  return null;
}

/* ───────────────────────── Плановый платёж, счёт, перенос даты (ФН-02, ФН-21) ───────────────────────── */

export function PlanSection({
  pr,
  canEditDraft,
  canEditPlan,
}: {
  pr: PaymentRequestView;
  /** Суть заявки: плановый платёж, счёт, назначение — тот, кто готовит заявки на оплату */
  canEditDraft: boolean;
  /** Дата, очерёдность и источник денег — ещё финансы проекта и финансовый менеджер */
  canEditPlan: boolean;
}) {
  const qc = useQueryClient();
  const editable = canEditDraft || canEditPlan;
  const draft = (pr.status === 'draft' || pr.status === 'approval') && canEditDraft;
  const contract = useQuery({
    queryKey: ['contracts', 'detail', pr.contractId],
    queryFn: () => api<ContractDetail>(`/contracts/${pr.contractId}`),
    enabled: Boolean(pr.contractId) && draft,
  });
  const [f, setF] = useState({
    scheduleRowId: pr.scheduleRowId ?? (pr.contractId ? NONE : ''),
    priority: pr.priority,
    fundingSource: (pr.fundingSource ?? '') as FundingSource | '',
    invoiceNumber: pr.invoiceNumber ?? '',
    invoiceDate: pr.invoiceDate ?? '',
    purpose: pr.purpose ?? '',
    plannedDate: pr.plannedDate ?? '',
    moveReason: '',
  });
  // Перенос с причиной — у переданной заявки; в черновике дата меняется свободно (так и на сервере)
  const sent = !['draft', 'approval'].includes(pr.status);
  const moving = sent && Boolean(pr.plannedDate) && (f.plannedDate || null) !== pr.plannedDate;
  const save = useMutation({
    mutationFn: () =>
      api<PaymentRequestView>(`/procurement/payment-requests/${pr.id}`, {
        method: 'PATCH',
        body: draft
          ? {
              ...(pr.contractId && f.scheduleRowId
                ? { scheduleRowId: f.scheduleRowId === NONE ? null : f.scheduleRowId }
                : {}),
              priority: f.priority,
              fundingSource: f.fundingSource || null,
              invoiceNumber: f.invoiceNumber.trim() || null,
              invoiceDate: f.invoiceDate || null,
              ...(f.purpose.trim() !== (pr.purpose ?? '') ? { purpose: f.purpose.trim() || null } : {}),
              plannedDate: f.plannedDate || null,
            }
          : {
              priority: f.priority,
              fundingSource: f.fundingSource || null,
              ...(moving
                ? { plannedDate: f.plannedDate || null, moveReason: f.moveReason.trim() }
                : (f.plannedDate || null) !== pr.plannedDate
                  ? { plannedDate: f.plannedDate || null }
                  : {}),
            },
      }),
    onSuccess: (v) => {
      refreshPayments(qc, v);
      setF((x) => ({ ...x, moveReason: '' }));
      toast('Заявка на оплату сохранена');
    },
  });
  const rows = openPaymentRows(contract.data);
  const current = contract.data?.schedule.find((r) => r.id === pr.scheduleRowId);
  const open = ['draft', 'hold', 'approval', 'sent'].includes(pr.status);
  return (
    <section className="flex flex-col gap-3 border-t border-line pt-4">
      <h3 className="text-[14px] font-semibold">Платёжный календарь и счёт</h3>
      {editable && open ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {draft && pr.contractId ? (
            <Field
              label="Плановый платёж договора"
              className="sm:col-span-2"
              hint="Из графика платежей договора (ФН-02)"
            >
              <Select value={f.scheduleRowId} onChange={(e) => setF({ ...f, scheduleRowId: e.target.value })}>
                {[...(current && !rows.includes(current) ? [current] : []), ...rows].map((r) => (
                  <option key={r.id} value={r.id}>
                    {scheduleOption(r)}
                  </option>
                ))}
                <option value={NONE}>вне графика платежей договора</option>
              </Select>
            </Field>
          ) : null}
          <Field
            label="Плановая дата оплаты"
            hint={sent ? 'Перенос — только с причиной; история переносов сохраняется' : undefined}
          >
            <Input
              type="date"
              value={f.plannedDate}
              onChange={(e) => setF({ ...f, plannedDate: e.target.value })}
            />
          </Field>
          <Field label="Очерёдность">
            <Select
              value={f.priority}
              onChange={(e) => setF({ ...f, priority: e.target.value as PaymentPriority })}
            >
              {Object.entries(PAYMENT_PRIORITIES).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          {moving ? (
            <Field label="Причина переноса" className="sm:col-span-2">
              <Input value={f.moveReason} onChange={(e) => setF({ ...f, moveReason: e.target.value })} />
            </Field>
          ) : null}
          <Field label="Источник денег">
            <Select
              value={f.fundingSource}
              onChange={(e) => setF({ ...f, fundingSource: e.target.value as FundingSource | '' })}
            >
              <option value="">не указан</option>
              {Object.entries(FUNDING_SOURCES).map(([k, t]) => (
                <option key={k} value={k}>
                  {t}
                </option>
              ))}
            </Select>
          </Field>
          {draft ? (
            <>
              <Field label="Номер счёта поставщика">
                <Input
                  value={f.invoiceNumber}
                  onChange={(e) => setF({ ...f, invoiceNumber: e.target.value })}
                />
              </Field>
              <Field label="Дата счёта">
                <Input
                  type="date"
                  value={f.invoiceDate}
                  onChange={(e) => setF({ ...f, invoiceDate: e.target.value })}
                />
              </Field>
              <Field
                label="Назначение платежа"
                className="sm:col-span-2"
                hint="По счёту и назначению портал находит возможную повторную оплату (ФН-21)"
              >
                <Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} />
              </Field>
            </>
          ) : null}
          <div className="sm:col-span-2">
            <Button
              variant="secondary"
              loading={save.isPending}
              disabled={moving && f.moveReason.trim().length < 3}
              onClick={() => save.mutate()}
            >
              Сохранить
            </Button>
          </div>
          <ErrorBox error={save.error} className="sm:col-span-2" />
        </div>
      ) : (
        <dl className="grid gap-x-6 gap-y-2 text-[14px] sm:grid-cols-2">
          <div>
            <dt className="text-[12px] text-ink-2">Плановый платёж договора</dt>
            <dd>{pr.scheduleLabel ?? (pr.contractId ? 'вне графика платежей договора' : '—')}</dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">Очерёдность и источник</dt>
            <dd>
              {PAYMENT_PRIORITIES[pr.priority]}
              {pr.fundingSource ? `, ${FUNDING_SOURCES[pr.fundingSource].toLowerCase()}` : ''}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">Счёт</dt>
            <dd>
              {pr.invoiceNumber
                ? `№ ${pr.invoiceNumber}${pr.invoiceDate ? ` от ${date(pr.invoiceDate)}` : ''}`
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-[12px] text-ink-2">Назначение платежа</dt>
            <dd className="[overflow-wrap:anywhere]">{pr.purpose ?? '—'}</dd>
          </div>
        </dl>
      )}
      {editable && open && !draft && pr.scheduleLabel ? (
        <p className="text-[13px] text-ink-2">Плановый платёж договора: {pr.scheduleLabel}</p>
      ) : null}
      {pr.moves.length ? (
        <div className="text-[13px]">
          <p className="text-ink-2">Переносы плановой даты:</p>
          <ul className="mt-1 flex flex-col gap-1">
            {pr.moves.map((m) => (
              <li key={m.at}>
                {date(m.from)} → {date(m.to)} — {m.reason}
                <span className="text-ink-2">
                  {' '}
                  ({m.byName ?? '—'}, {dateTime(m.at)})
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
